import { IssueHardwareType, IssueSavingsStatus } from '@kcftech/prometheus-client';
import {
    CreatedByFilter,
    FlaggedFilter,
    HealthRangeFilter,
    PriorityFilter,
    ResolutionFilter,
    SeverityFilter,
    StatusFilter,
    TimeRangeFilter,
    VisibilityFilter,
} from 'shared/issue_filters_context/types';
import { valueInList } from 'utils/object-validation';

/**
 * Shared validation constants for issue filters.
 * These constants define the valid values for each filter type.
 */
export const VALID_PRIORITY_FILTERS: PriorityFilter[] = [
    'critical',
    'high',
    'medium',
    'low',
    'minimal',
];

export const VALID_SEVERITY_FILTERS: SeverityFilter[] = ['severe', 'moderate', 'low'];

export const VALID_STATUS_FILTERS: StatusFilter[] = [
    'closed',
    'new',
    'not-an-issue',
    'open',
    'wont-fix',
];

export const VALID_SAVINGS_FILTERS = Object.values(IssueSavingsStatus);

export const VALID_TIME_RANGE_FILTERS: TimeRangeFilter[] = [
    'past-12-hours',
    'past-day',
    'past-month',
    'past-week',
];

export const VALID_HEALTH_RANGE_FILTERS: HealthRangeFilter[] = ['added', 'not-added'];

export const VALID_ISSUE_HARDWARE_TYPES = Object.values(IssueHardwareType);

export const VALID_VISIBILITY_FILTERS: VisibilityFilter[] = ['kcf-only', 'public'];

export const VALID_CREATED_BY_FILTERS: CreatedByFilter[] = [
    'user-generated',
    'system-generated',
];

export const VALID_FLAGGED_FILTERS: FlaggedFilter[] = [
    'flagged',
    'flagged-with-notifications',
];

export const VALID_RESOLUTION_FILTERS: ResolutionFilter[] = ['resolved'];

/**
 * Shared validation functions for issue filters.
 * These functions validate that a value is a valid filter value.
 */
export const isValidPriorityFilter = (value: unknown): value is PriorityFilter =>
    valueInList(value, VALID_PRIORITY_FILTERS);

export const isValidSeverityFilter = (value: unknown): value is SeverityFilter =>
    valueInList(value, VALID_SEVERITY_FILTERS);

export const isValidStatusFilter = (value: unknown): value is StatusFilter =>
    valueInList(value, VALID_STATUS_FILTERS);

export const isValidSavingsFilter = (value: unknown): value is IssueSavingsStatus =>
    valueInList(value, VALID_SAVINGS_FILTERS);

export const isValidTimeRangeFilter = (value: unknown): value is TimeRangeFilter =>
    valueInList(value, VALID_TIME_RANGE_FILTERS);

export const isValidHealthRangeFilter = (value: unknown): value is HealthRangeFilter =>
    valueInList(value, VALID_HEALTH_RANGE_FILTERS);

export const isValidIssueHardwareType = (value: unknown): value is IssueHardwareType =>
    valueInList(value, VALID_ISSUE_HARDWARE_TYPES);

export const isValidVisibilityFilter = (value: unknown): value is VisibilityFilter =>
    valueInList(value, VALID_VISIBILITY_FILTERS);

export const isValidFlaggedFilter = (value: unknown): value is FlaggedFilter =>
    valueInList(value, VALID_FLAGGED_FILTERS);

export const isValidCreatedByFilter = (value: unknown): value is CreatedByFilter =>
    valueInList(value, VALID_CREATED_BY_FILTERS);

export const isValidResolutionFilter = (value: unknown): value is ResolutionFilter =>
    valueInList(value, VALID_RESOLUTION_FILTERS);
